/**
 * `prioritizeSignals` — client copy of
 * mini-apps/action-view/hooks/usePrioritizedActions.ts's `prioritizeActions`
 * (the pure ranking core), re-keyed onto SignalRow (bead .6) /
 * PriorityRankingEntry (bead .8) field names — never imported
 * (mini-apps/CLAUDE.md); kept in lockstep by convention and matching tests
 * on both sides.
 *
 * Re-keying: type = signal_source_type, subtype = signal_source_subtype,
 * playType = play_type, step number = step_number, step type =
 * signal_tactic, due = due_date, surfaced = event_date.
 *
 * Canonical order: typeMinRank -> type label -> step number ASC (null last)
 * -> stepTypeRank -> step label -> rankOf (unconfigured last) -> overdue
 * first / most overdue first -> surfaced DESC. Deliberately NO fit-score
 * tiebreak — that field does not exist on a Signals row (documented gap, PR
 * description notes it as the known accepted difference from Action View).
 *
 * Deviation from Action View (fix-plan-batch3.md §1.4/D-D): the two LABEL
 * tiebreaks (`compareLabelKeys`) are not in `usePrioritizedActions.ts:134-141`.
 * Every unconfigured type — and blank/NULL Type — ties at `typeMinRank`
 * Infinity, so without them numbering fell to step number and interleaved
 * types (`1P Event` #1, `No Type` #2, `1P Event` #3): the min-priority group
 * order could then read 1, 3, 2. With them, rows of one type (blank types
 * together, last) are numbered contiguously, and within (type, step number)
 * rows of one step label likewise, so the default Type → Step # → Step
 * tree reads 1..n top to bottom for one rep. Configured types are
 * unaffected (their `typeMinRank` is distinct per type). Action View keeps
 * the interleaving; PR 3 retires it.
 *
 * Manual/auto split is on `signal_tactic` containing "auto" (case
 * insensitive), manual numbered before auto, independently per assignee
 * bucket (`assignee_id ?? '__unassigned__'`) — numbering is over ALL rows
 * passed in (every loaded page), never restarting per page. `action_state`
 * in Archived/Completed always yields `priority: null` (kept in the output).
 * Unconfigured types are never deactivated — they sort last
 * (`typeMinRank`/`rankOf` both `?? Infinity`) but are still numbered.
 *
 * THIS FILE NEVER DROPS A ROW (round-1 fix D-O / R22). The SERVER
 * (`signalsUniverseWhere`) is the visibility authority. It diverges from
 * `prioritizeActions`, which excludes deactivated rows outright, on
 * purpose: the SQL exclusion matches only EXACT `type|subtype|playType`
 * keys while `isDeactivated` here also honours wildcards, so a single
 * inactive wildcard Ranker entry used to blank rows the server had already
 * decided were visible — an empty table with no empty-state explaining it.
 * A deactivated row now gets `priority: null` and is kept, exactly like a
 * terminal one: the client may un-NUMBER a row, never delete one.
 *
 * (Wildcard deactivation is still not honoured in SQL. PR 2's Ranker admin
 * either blocks wildcard rows or teaches `signalsUniverseWhere` the two
 * wildcard predicates.)
 */
import type { SignalRow } from '@shared/flow/signal-view';
import type { PriorityRankingEntry } from '@shared/schemas/priority-ranking';
import { buildCanonicalRanking, type CanonicalRanking } from './rankingFallback';
import { stepTypeRank } from './stepTypeRank';
import { isBlank } from './signalGroupValue';
import { parseCalendarDay } from '../../../lib/calendarDay';

export interface PrioritizedSignalRow extends SignalRow {
  priority: number | null;
}

const TERMINAL_ACTION_STATES = new Set(['Archived', 'Completed']);

function isAutoStep(row: SignalRow): boolean {
  return (row.signal_tactic ?? '').toLowerCase().includes('auto');
}

interface DueInfo {
  state: 'overdue' | 'due-today' | 'upcoming';
  dueStart: Date;
}

/**
 * Local-calendar-day overdue handling (copied from
 * mini-apps/action-view/utils.ts's `parseGongDueDate`, re-keyed onto
 * `due_date` and taking an explicit `today` instead of defaulting to
 * `new Date()` — the Signals ranker must be deterministic given a fixed
 * `today`, not wall-clock time). A due date is only "overdue" starting the
 * next local day; on the due day itself it's 'due-today' (non-overdue).
 */
function resolveDueState(dueDate: string | null | undefined, today: Date): DueInfo | null {
  if (!dueDate) return null;
  const dueStart = parseCalendarDay(dueDate);
  if (!dueStart) return null;
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const dayDiff = Math.round((todayStart.getTime() - dueStart.getTime()) / 86_400_000);
  const state: DueInfo['state'] = dayDiff > 0 ? 'overdue' : dayDiff === 0 ? 'due-today' : 'upcoming';
  return { state, dueStart };
}

/** One collator for every label comparison — `localeCompare(…, 'en-US')` semantics without a per-call Intl lookup. */
const EN_US_COLLATOR = new Intl.Collator('en-US');

/**
 * A TOTAL order over group labels (fix-plan-batch3.md D-D): both blank
 * (`isBlank` — null, undefined, all-whitespace: one "No …" group) tie; blank
 * after non-blank; equal strings tie; otherwise en-US collation, and a
 * code-unit tiebreak where collation calls two distinct strings equal — so
 * distinct labels never tie and every label's rows stay contiguous.
 */
export function compareLabelKeys(x: string | null | undefined, y: string | null | undefined): number {
  const bx = isBlank(x);
  const by = isBlank(y);
  if (bx && by) return 0;
  if (bx) return 1;
  if (by) return -1;
  const sx = String(x);
  const sy = String(y);
  if (sx === sy) return 0;
  return EN_US_COLLATOR.compare(sx, sy) || (sx < sy ? -1 : 1);
}

/**
 * Canonical comparator: typeMinRank -> type label (D-D) -> step number ASC ->
 * stepTypeRank -> step label (D-D) -> rankOf -> overdue first/most-overdue-first
 * -> surfaced DESC.
 */
function compareRows(a: SignalRow, b: SignalRow, canonical: CanonicalRanking, today: Date): number {
  const ta = canonical.typeMinRank(a.signal_source_type);
  const tb = canonical.typeMinRank(b.signal_source_type);
  if (ta !== tb) return ta < tb ? -1 : 1;
  // D-D: unconfigured (and blank) types all tie at Infinity above — number
  // them one type at a time, never interleaved by step.
  const typeLabel = compareLabelKeys(a.signal_source_type, b.signal_source_type);
  if (typeLabel !== 0) return typeLabel;

  const stepA = a.step_number ?? Infinity;
  const stepB = b.step_number ?? Infinity;
  if (stepA !== stepB) return stepA < stepB ? -1 : 1;

  const sa = stepTypeRank(a.signal_tactic);
  const sb = stepTypeRank(b.signal_tactic);
  if (sa !== sb) return sa - sb;
  // D-D: two tactic labels in one stepTypeRank bucket number contiguously.
  const stepLabel = compareLabelKeys(a.signal_tactic, b.signal_tactic);
  if (stepLabel !== 0) return stepLabel;

  const ra = canonical.rankOf(a.signal_source_type, a.signal_source_subtype, a.play_type) ?? Infinity;
  const rb = canonical.rankOf(b.signal_source_type, b.signal_source_subtype, b.play_type) ?? Infinity;
  if (ra !== rb) return ra - rb;

  const dueA = resolveDueState(a.due_date, today);
  const dueB = resolveDueState(b.due_date, today);
  const overA = dueA?.state === 'overdue';
  const overB = dueB?.state === 'overdue';
  if (overA !== overB) return overA ? -1 : 1;
  if (overA && overB) {
    // Oldest due date first — most overdue is most urgent. TS narrows
    // `dueA`/`dueB` to non-null here via aliased-condition control-flow
    // analysis on `overA`/`overB` (both true implies both `dueA?.state`/
    // `dueB?.state` were reachable, i.e. neither operand was null).
    const dd = dueA.dueStart.getTime() - dueB.dueStart.getTime();
    if (dd !== 0) return dd;
  }

  // Surfaced DESC — newest first. (No fit-score tiebreak — not on the row.)
  return new Date(b.event_date ?? 0).getTime() - new Date(a.event_date ?? 0).getTime();
}

/**
 * Assign priorities to every row, per-assignee, manual before auto, over the
 * ENTIRE supplied array (never restarting per page). Returns EVERY input
 * row, in input order — a row whose Ranker entry is deactivated, and an
 * Archived/Completed row, both get `priority: null` and stay (D-O; see the
 * module header for why this file is not allowed to drop a row).
 * Archived/Completed rows are exempt from deactivation resolution entirely,
 * mirroring `prioritizeActions`'s unconditional archived bucket.
 */
export function prioritizeSignals(
  rows: SignalRow[],
  entries: PriorityRankingEntry[],
  today: Date,
): PrioritizedSignalRow[] {
  const canonical = buildCanonicalRanking(entries);

  interface Indexed { index: number; row: SignalRow }
  const byAssignee = new Map<string, { manual: Indexed[]; auto: Indexed[] }>();
  const priorityByIndex = new Map<number, number | null>();

  rows.forEach((row, index) => {
    if (TERMINAL_ACTION_STATES.has(row.action_state)) {
      priorityByIndex.set(index, null);
      return;
    }
    const assigneeKey = row.assignee_id ?? '__unassigned__';
    let bucket = byAssignee.get(assigneeKey);
    if (!bucket) {
      bucket = { manual: [], auto: [] };
      byAssignee.set(assigneeKey, bucket);
    }
    (isAutoStep(row) ? bucket.auto : bucket.manual).push({ index, row });
  });

  for (const bucket of byAssignee.values()) {
    bucket.manual.sort((a, b) => compareRows(a.row, b.row, canonical, today));
    bucket.auto.sort((a, b) => compareRows(a.row, b.row, canonical, today));

    let rank = 1;
    for (const { index, row } of bucket.manual) {
      if (canonical.isDeactivated(row.signal_source_type, row.signal_source_subtype, row.play_type)) {
        priorityByIndex.set(index, null);
        continue;
      }
      priorityByIndex.set(index, rank++);
    }
    for (const { index, row } of bucket.auto) {
      if (canonical.isDeactivated(row.signal_source_type, row.signal_source_subtype, row.play_type)) {
        priorityByIndex.set(index, null);
        continue;
      }
      priorityByIndex.set(index, rank++);
    }
  }

  // Every input row comes back out, in the caller's order (D-O). Ordering
  // unnumbered rows after numbered ones is `arrangeSignalsClientSide`'s job —
  // it already sinks every null last, in both directions, for terminal
  // rows; a deactivated row is now identical to those.
  return rows.map((row, index) => ({ ...row, priority: priorityByIndex.get(index) ?? null }));
}
